// Types de l'offre sans dépendance serveur, importables côté client
// (même rôle que lib/leads-shared.ts pour le pipeline de leads).
//
// Une ligne `offer_profiles` est une offre : son adresse, son nom, la
// compréhension qu'en a l'agent et son ICP.

/**
 * Un fait public qui dit si une organisation est un bon compte pour l'offre
 * (taille, outil en place, organisation de l'équipe concernée…). L'analyse de
 * l'offre les propose, l'utilisateur les corrige ; l'enquête de chaque lead y
 * répond (`LeadKeyFact`).
 */
export type QualificationCriterion = {
  label: string;
  /** Ce qui, observé, fait de l'organisation un bon compte. */
  good_sign: string;
  /** Décisif : montré en premier, et sur la fiche du lead avant les autres. */
  decisive: boolean;
  /** Vocabulaire à chercher (noms d'outils, de postes…), pour l'enquête. */
  search_terms: string[];
};

export const MAX_QUALIFICATION_CRITERIA = 6;

/**
 * Nettoie une liste de critères venue du modèle ou du client : libellé
 * obligatoire, champs bornés, doublons de libellé retirés.
 */
export function cleanQualificationCriteria(value: unknown): QualificationCriterion[] {
  if (!Array.isArray(value)) return [];
  const text = (input: unknown, max: number) =>
    typeof input === "string" ? input.replace(/\s+/g, " ").trim().slice(0, max) : "";
  const seen = new Set<string>();
  const criteria: QualificationCriterion[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;
    const label = text(record.label, 80);
    const key = label.toLowerCase();
    if (!label || seen.has(key)) continue;
    seen.add(key);
    const terms = Array.isArray(record.search_terms) ? record.search_terms : [];
    criteria.push({
      label,
      good_sign: text(record.good_sign, 240),
      decisive: record.decisive === true,
      search_terms: [...new Set(terms.map((term) => text(term, 60)).filter(Boolean))].slice(0, 6),
    });
    if (criteria.length === MAX_QUALIFICATION_CRITERIA) break;
  }
  return criteria;
}

export type OfferProfile = {
  id: string;
  url: string;
  offer_name: string | null;
  offer_summary: string | null;
  priority_segment: string | null;
  decision_maker: string | null;
  favorable_event: string | null;
  exclusions: string | null;
  user_notes: string | null;
  qualification_criteria: QualificationCriterion[];
  edited_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * Ce qu'il faut savoir d'une offre pour en parler à l'agent — sans l'identité
 * de la ligne. La prospection sortante (`lib/prospect-research.ts`) s'en sert
 * pour décrire l'offre d'un prospect, qui n'a pas de profil en base.
 */
export type OfferBriefing = Pick<
  OfferProfile,
  | "offer_name"
  | "offer_summary"
  | "priority_segment"
  | "decision_maker"
  | "favorable_event"
  | "exclusions"
  | "user_notes"
  | "edited_at"
> & {
  /** Absents pour un prospect du SDR, qui n'a pas de profil en base. */
  qualification_criteria?: QualificationCriterion[];
};

/** Champs modifiables depuis les réglages (et validés par l'API). */
export const OFFER_PROFILE_EDITABLE_FIELDS = [
  "offer_name",
  "offer_summary",
  "priority_segment",
  "decision_maker",
  "favorable_event",
  "exclusions",
  "user_notes",
] as const;

export type OfferProfileEditableField = (typeof OFFER_PROFILE_EDITABLE_FIELDS)[number];

export type OfferProfilePatch = Partial<Record<OfferProfileEditableField, string>>;
